
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingWhatsApp from '@/components/FloatingWhatsApp';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  message: z.string().min(10, {
    message: "Message must be at least 10 characters.",
  }),
});

const Contact = () => {
  const { t } = useLanguage();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      console.log('Attempting to insert contact submission:', values);
      
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert([{
          name: values.name,
          email: values.email,
          message: values.message
        }]);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Contact submission successful:', data);
      toast({
        title: "Message sent",
        description: "We'll be in touch shortly.",
      });
      form.reset();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-black flex items-center">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 py-16 pt-24">
          <div className="max-w-xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 text-center animate-fade-in-up">
              {t('contact.title')}
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 text-center animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              {t('contact.description')}
            </p>
            
            <div className="bg-dark-300 p-8 rounded-lg animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">{t('contact.name')}</FormLabel>
                        <FormControl>
                          <Input placeholder="" className="bg-dark-200 border-gray-700 text-white" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Email</FormLabel>
                        <FormControl>
                          <Input placeholder="" className="bg-dark-200 border-gray-700 text-white" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">{t('contact.message')}</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="" 
                            className="bg-dark-200 border-gray-700 text-white min-h-[150px]" 
                            {...field} 
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-white py-6 font-medium">
                    {t('contact.send')}
                  </Button>
                </form>
              </Form>
            </div>
            
            <p className="text-gray-400 mt-8 text-center animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              {t('contact.email')}
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
};

export default Contact;
